import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FormControl, InputLabel, MenuItem, Select } from "@mui/material";

import { useStoreDispatch, useStoreSelector } from "../../../../../../../hooks/StoreHooks";
import { ChiusuraType, VetroType, TeloType, LatoType, Aperture, Verso } from "../../../../../../models/pergola_configuration";
import { usePergolaConfigurationBuilderActions } from "../../../../../../../store/reducers/pergola_configuration_builder";

import { FrmChiusuraAndromeda } from "../../chiusure/frm_chiusure_andromeda";
import { TendeZip } from "../../chiusure/frm_chiusura_zip";
import { FrmChiusuraMaia } from "../../chiusure/frm_chiusura_maia";

type Props = {
  lato: "Retro" | "Fronte" | "Destra" | "Sinistra";
  segmento: number;
};

export const ChiusuraChoice = ({ lato,segmento }: Props) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useStoreDispatch();
  const {pergolaConfigurationBuilder } = useStoreSelector(store => store);
  const { setStepActive, setStepComplete, setPergolaSide,removePergolaSideSingleSegment } = usePergolaConfigurationBuilderActions();
  const latoScelto = pergolaConfigurationBuilder.lati.find(l => l.direzione === lato &&l.segmento === segmento);
  const [isSubmit, setIsSubmit] = useState(false);
  const larghezzaSoletta = (pergolaConfigurationBuilder.configuration?.dimensioni.larghezza ?? 2000) / 1000;
  const lunghezzaSoletta = (pergolaConfigurationBuilder.configuration?.dimensioni.profondita ?? 4000) / 1000;
  let nstacchi = Math.floor(larghezzaSoletta / 4.1);
  const tolleranza = 1e-9;

  if (Math.abs(nstacchi * 4.1 - larghezzaSoletta) > tolleranza) {
      nstacchi += 1;
  }
  let nstacchiy = Math.floor(lunghezzaSoletta / 7) ;
  if (Math.abs(nstacchiy * 7 - lunghezzaSoletta) > tolleranza) {
      nstacchiy += 1;
  }
  let lunghezzaVetrata = 0;
  const latoValues = latoScelto?.tipoLato.tipologia as any || {};
  if(latoScelto?.direzione === "Fronte"||latoScelto?.direzione === "Retro"){
    lunghezzaVetrata = ((larghezzaSoletta /nstacchi)*1000) - 270;
  }else{
    lunghezzaVetrata = ((lunghezzaSoletta /nstacchiy)*1000) - 270;
  }
  useEffect(() => {
    if (!id || isNaN(Number(id)) || !pergolaConfigurationBuilder.configuration) {
      navigate("/configurations");
    } else {
      console.log("LUNGHEZZA VETRO",lunghezzaVetrata,"-",larghezzaSoletta);
    }
  }, [id, navigate, pergolaConfigurationBuilder.configuration, dispatch, setStepActive]);
  const handleChange = (field: string, value: any) => {
    const updatedValues = { ...latoValues, [field]: value };
    if (field === "apertura" && latoValues.tipo_chiusura === ChiusuraType.vetrate_scorrevoli_maia) {
      if (value === Aperture.centrale) {
        updatedValues.sistema_chiusura = "ACCMBV16";
        updatedValues.verso = undefined;
      } else {
        updatedValues.sistema_chiusura = "ACCMBV12";
      }
    }
    if (field === "tipo_chiusura") {
      if (value === ChiusuraType.vetrate_ad_impacchettamento_andromeda || value === ChiusuraType.vetrate_scorrevoli_maia) {
        updatedValues.tipo_vetro = VetroType.vetro_temperato_10mm;
        delete updatedValues.tipo_telo;
        delete updatedValues.codice_telo;
      } else if (value === ChiusuraType.tende_zip) {
        updatedValues.tipo_telo = TeloType.teloOscurantePOLI;
        updatedValues.codice_telo = "";
        delete updatedValues.tipo_vetro;
        delete updatedValues.apertura;
        delete updatedValues.sistema_chiusura;
      }else{
        delete updatedValues.tipo_telo;
        delete updatedValues.codice_telo;
        delete updatedValues.tipo_vetro;
        delete updatedValues.apertura;
        delete updatedValues.sistema_chiusura;
      }
    }
    dispatch(setPergolaSide({ direzione: lato,segmento: segmento, tipoLato: { tipologia: updatedValues } }));
    if(value === ChiusuraType.nessuna)dispatch(removePergolaSideSingleSegment({ direzione: lato,segmento: segmento}));
  };

  useEffect(() => {
    if (latoValues.tipo_chiusura === ChiusuraType.vetrate_scorrevoli_maia) {
      const defaultApertura = latoValues.apertura || Aperture.laterale;
      const needsUpdate =
        !latoValues.apertura ||
        !latoValues.sistema_chiusura ||
        (defaultApertura === Aperture.laterale && !latoValues.verso);
      if (needsUpdate) {
        const updatedValues = {
          ...latoValues,
          apertura: defaultApertura,
          sistema_chiusura: latoValues.sistema_chiusura || "ACCMBV12",
          ...(defaultApertura === Aperture.laterale && !latoValues.verso ? { verso: Verso.destra } : {}),
        };
        dispatch(setPergolaSide({ direzione: lato, segmento: segmento, tipoLato: { tipologia: updatedValues } }));
      }
    }
  }, [latoValues.tipo_chiusura]);

  const submit = async () => {
    setIsSubmit(true);
    try {
      dispatch(setStepComplete({ stepSlug: "chiusure", isComplete: true }));
      navigate(`/pergola-configurations/${id}/illuminazioni`);
    } catch (e: any) {
      setIsSubmit(false);
      alert(`Errore: ${e?.message || e}`);
    }
  };

  return (
    <div className="row h-full m-0">
      <div className="p-4">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
        >
          <FormControl fullWidth className="mt-3">
            <InputLabel id="tipo_chiusura_label">Tipo di chiusura</InputLabel>
            <Select
              labelId="tipo_chiusura_label"
              id="tipo_chiusura"
              label = "Tipo di chiusura"
              value={latoValues.tipo_chiusura || ChiusuraType.nessuna}
              onChange={(e) => handleChange("tipo_chiusura", e.target.value)}
            >
              <MenuItem value={ChiusuraType.nessuna}>Nessuna</MenuItem>
              <MenuItem value={ChiusuraType.vetrate_ad_impacchettamento_andromeda}>Vetrate ad Impacchettamento ANDROMEDA</MenuItem>
              <MenuItem value={ChiusuraType.vetrate_scorrevoli_maia}>Vetrate Scorrevoli MAIA</MenuItem>
              <MenuItem value={ChiusuraType.tende_zip}>Tende zip</MenuItem>
            </Select>
          </FormControl>

          {/* Andromeda */}
          {latoValues.tipo_chiusura === ChiusuraType.vetrate_ad_impacchettamento_andromeda && (
            <FrmChiusuraAndromeda
              tipoChiusura={latoValues.tipo_chiusura}
              tipoVetro={latoValues.tipo_vetro}
              coloreTelaioNsRal={latoValues.coloreTelaioNsRal}
              coloreTelaioRal={latoValues.coloreTelaioRal}
              sistema_chiusura={latoValues.sistema_chiusura}
              lunghezzaVetrata = {lunghezzaVetrata}
              apertura={latoValues.apertura || Aperture.laterale}
              verso = {latoValues.verso}
              verso_impacchettamento = {latoValues.verso_impacchettamento}
              accessori={latoValues.accessori || []}
              vie={latoValues.vie || 2}
              handleChange={handleChange}
            />
          )}

          {/* Maia */}
          {latoValues.tipo_chiusura === ChiusuraType.vetrate_scorrevoli_maia && (
            <FrmChiusuraMaia
              tipoChiusura={latoValues.tipo_chiusura}
              tipoVetro={latoValues.tipo_vetro}
              apertura={latoValues.apertura || Aperture.laterale}
              coloreTelaioNsRal={latoValues.coloreTelaioNsRal}
              coloreTelaioRal={latoValues.coloreTelaioRal}
              sistema_chiusura={latoValues.sistema_chiusura}
              lunghezzaVetrata = {lunghezzaVetrata}
              verso = {latoValues.verso}
              vie={latoValues.vie || 2}
              accessori={latoValues.accessori || []}
              handleChange={handleChange}
            />
          )}

          {/* Tende Zip */}
          {latoValues.tipo_chiusura === ChiusuraType.tende_zip && (
            <TendeZip
              tipoTelo={latoValues.tipo_telo}
              codiceTelo={latoValues.codice_telo}
              accessori={latoValues.accessori || []}
              lato={lato}
              handleChange={handleChange}
            />
          )}
        </form>
      </div>
    </div>
  );
};
